import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { strapiGet, strapiPost, strapiPut } from '@/lib/strapi'
import { useAppStore } from '@/lib/store/useAppStore'
import { toast } from 'sonner'
import type { Restaurant } from '@/lib/queries/useRestaurants'
import type { OpeningHour } from '@/lib/types/openingHour'
import { STRAPI_BASE_URL } from '@/config/api'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomQuestion {
  id: string
  label: string
  type: 'text' | 'select'
  options?: string[]
  required: boolean
}

export interface BookingPageConfig {
  id: number
  documentId: string
  bookosId?: string
  enabled: boolean
  coverImage?: {
    id: number
    url: string
    formats?: Record<string, { url: string }>
  } | null
  logo?: {
    id: number
    url: string
    formats?: Record<string, { url: string }>
  } | null
  termsAndConditions?: string
  requireTerms: boolean
  allowReservations: boolean
  showEmail: boolean
  requireEmail: boolean
  allowPreOrder: boolean
  allowCollectionOrders: boolean
  allowScheduledCollectionOrders: boolean
  scheduledOrderLeadMinutes: number
  deliveryOrderSlotIntervalMinutes: number
  deliveryOrderSlotCapacity: number
  customQuestions?: CustomQuestion[]
  restaurant?: { documentId: string }
}

export interface PublicRestaurant extends Restaurant {
  bookingPageConfig?: BookingPageConfig | null
}

// ─── Staff: Booking Page Config ───────────────────────────────────────────────

export function useBookingPageConfig() {
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useQuery({
    queryKey: ['bookingPageConfig', restaurantId],
    queryFn: () =>
      strapiGet<BookingPageConfig[]>('/api/booking-page-configs', {
        filters: { restaurant: { documentId: { $eq: restaurantId } } },
        populate: { coverImage: true, logo: true, restaurant: true },
      }),
    enabled: !!restaurantId,
    staleTime: 5 * 60_000,
  })
}

export function useUpsertBookingPageConfig() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (
      data: Partial<BookingPageConfig> & { coverImageId?: number | null; logoId?: number | null },
    ) => {
      const restaurantId = useAppStore.getState().restaurantId
      // Check if config already exists
      const existing = await strapiGet<BookingPageConfig[]>('/api/booking-page-configs', {
        filters: { restaurant: { documentId: { $eq: restaurantId } } },
      })
      const configs = (existing as any).data ?? []
      const existingConfig = Array.isArray(configs) ? configs[0] : null

      const payload: Record<string, unknown> = {
        enabled: data.enabled ?? false,
        termsAndConditions: data.termsAndConditions ?? '',
        requireTerms: data.requireTerms ?? true,
        allowReservations: data.allowReservations ?? true,
        showEmail: data.showEmail ?? true,
        requireEmail: data.requireEmail ?? false,
        allowPreOrder: data.allowPreOrder ?? false,
        allowCollectionOrders: data.allowCollectionOrders ?? false,
        allowScheduledCollectionOrders: data.allowScheduledCollectionOrders ?? true,
        scheduledOrderLeadMinutes: data.scheduledOrderLeadMinutes ?? 30,
        deliveryOrderSlotIntervalMinutes: data.deliveryOrderSlotIntervalMinutes ?? 0,
        deliveryOrderSlotCapacity: data.deliveryOrderSlotCapacity ?? 0,
        customQuestions: data.customQuestions ?? [],
        restaurant: restaurantId,
      }

      if ('coverImageId' in data) {
        payload.coverImage = data.coverImageId ?? null
      }
      if ('logoId' in data) {
        payload.logo = data.logoId ?? null
      }

      if (existingConfig) {
        return strapiPut<BookingPageConfig>(
          `/api/booking-page-configs/${existingConfig.documentId}`,
          { data: payload },
        )
      } else {
        return strapiPost<BookingPageConfig>('/api/booking-page-configs', {
          data: {
            ...payload,
            bookosId: `bpc-${Date.now()}`,
          },
        })
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookingPageConfig'] })
      toast.success('Customer page settings saved')
    },
    onError: () => toast.error('Failed to save customer page settings'),
  })
}

// ─── Public: no auth, identify by slug ───────────────────────────────────────

export function usePublicRestaurant(slug: string) {
  return useQuery({
    queryKey: ['publicRestaurant', slug],
    queryFn: () =>
      strapiGet<PublicRestaurant[]>('/api/restaurants', {
        filters: { slug: { $eq: slug } },
        populate: {
          address: true,
          bookingPageConfig: { populate: { coverImage: true, logo: true } },
        },
      }),
    enabled: !!slug,
    staleTime: 5 * 60_000,
  })
}

export function usePublicOpeningHours(restaurantDocumentId: string | null) {
  return useQuery({
    queryKey: ['publicOpeningHours', restaurantDocumentId],
    queryFn: () =>
      strapiGet<OpeningHour[]>('/api/opening-hours', {
        filters: { restaurant: { documentId: { $eq: restaurantDocumentId } } },
        pagination: { pageSize: 100 },
        sort: ['day:asc', 'open:asc'],
      }),
    enabled: !!restaurantDocumentId,
    staleTime: 10 * 60_000,
  })
}

// ─── Public booking submission ─────────────────────────────────────────────

interface PublicBookingPayload {
  restaurantSlug: string
  date: string
  time: string
  people: number
  asap?: boolean
  bookingKind?: 'reservation' | 'order'
  guest: { name: string; phone: string; email?: string }
  message?: string
  customAnswers?: Record<string, unknown>
  acceptedTerms: boolean
  source?: string
  fulfillmentMethod?: 'pickup' | 'delivery'
  deliveryAddress?: string
  deliveryLocation?: string
  deliveryLatitude?: number
  deliveryLongitude?: number
  tableDocumentIds?: string
  preOrderItems?: {
    menuItemDocumentId: string
    quantity: number
    course?: string
    notes?: string
    selectedOptions?: unknown[]
  }[]
}

interface PublicBookingResult {
  documentId: string
  bookosId: string
  date: string
  time: string
  people: number
  status: string
  deliveryDistanceKm?: number
  deliveryFee?: number
  deliveryChargePerKm?: number
  deliveryFlatFee?: number
  total?: number
  statusUrl?: string | null
}

export interface PublicDeliveryQuote {
  enabled: boolean
  originConfigured: boolean
  destinationConfigured: boolean
  distanceKm: number
  deliveryFee: number
  deliveryChargePerKm: number
  deliveryFlatFee: number
  method: 'route' | 'estimated' | 'flat' | 'disabled'
  currency: string
}

export interface PublicOrderSettings {
  acceptAsapOrders: boolean
  currentDate: string
  deliveryChargePerKm: number
  deliveryFlatFee: number
  deliveryOrderSlotIntervalMinutes: number
  deliveryOrderSlotCapacity: number
}

export interface PublicOrderAvailability {
  open: boolean
  date: string
  fulfillmentMethod: 'pickup' | 'delivery'
  slots: string[]
  asapAvailable: boolean
  asapSlot: string | null
  slotCapacityEnabled: boolean
  slotIntervalMinutes: number | null
  slotCapacity: number | null
}

export function usePublicOrderSettings(slug: string) {
  return useQuery({
    queryKey: ['publicOrderSettings', slug],
    queryFn: () => strapiGet<PublicOrderSettings>('/api/public/order-settings', { slug }),
    enabled: !!slug,
    staleTime: 60_000,
  })
}

export function usePublicOrderAvailability(
  slug: string,
  date: string,
  fulfillmentMethod: 'pickup' | 'delivery',
  enabled = true,
) {
  return useQuery({
    queryKey: ['publicOrderAvailability', slug, date, fulfillmentMethod],
    queryFn: async () => {
      const response = await strapiGet<PublicOrderAvailability>('/api/public/order-availability', {
        slug,
        date,
        fulfillmentMethod,
      })
      return response.data
    },
    enabled: enabled && !!slug && !!date,
    staleTime: 10_000,
    refetchInterval: enabled ? 15_000 : false,
  })
}

export async function fetchPublicDeliveryQuote(slug: string, latitude: number, longitude: number) {
  return strapiGet<PublicDeliveryQuote>('/api/public/delivery-quote', {
    slug,
    latitude,
    longitude,
  })
}

export function useCreatePublicRequest() {
  return useMutation({
    mutationFn: async (payload: PublicBookingPayload) => {
      const endpoint = payload.bookingKind === 'order' ? '/api/public/orders' : '/api/public/reservations'
      const res = await fetch(`${STRAPI_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error?.message ?? 'Failed to submit booking')
      }
      return res.json() as Promise<{ data: PublicBookingResult }>
    },
  })
}

export const useCreatePublicBooking = useCreatePublicRequest

// ─── Image upload ─────────────────────────────────────────────────────────────

export async function uploadImage(file: File): Promise<number> {
  const token = (await import('@/utils/storage')).storage.getToken()

  const form = new FormData()
  form.append('files', file)

  const res = await fetch(`${STRAPI_BASE_URL}/api/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  if (!res.ok) throw new Error('Image upload failed')
  const data = await res.json()
  return data[0]?.id as number
}

export const uploadCoverImage = uploadImage
